// SectorRotation.js
import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './SectorRotation.css';
import { API_BASE_URL } from '../config';
import RefreshButton from './RefreshButton';

// Helper function to safely get values with fallback
const getValue = (value, fallback = null) => {
  return value !== undefined && value !== null ? value : fallback;
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Cache manager
const cacheManager = {
  defaultExpiry: 5 * 60 * 1000,
  _storage: new Map(),
  
  getKey: (endpoint, params = {}) => {
    const paramsStr = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `${endpoint}?${paramsStr}`;
  },
  
  get: function(key) {
    const cached = this._storage.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > (cached.expiry || this.defaultExpiry)) {
      this._storage.delete(key);
      return null;
    }
    
    return cached.data;
  },
  
  set: function(key, data, options = {}) {
    this._storage.set(key, {
      data,
      timestamp: Date.now(),
      expiry: options.expiry || this.defaultExpiry
    });
    return data;
  },
  
  clear: function(key) {
    this._storage.delete(key);
  },
  
  clearAll: function() {
    this._storage.clear();
  },
  
  cleanup: function() {
    const now = Date.now();
    for (const [key, { expiry = this.defaultExpiry, timestamp }] of this._storage.entries()) {
      if (now - timestamp > expiry) {
        this._storage.delete(key);
      }
    }
  }
};

// Run cleanup every minute
setInterval(() => cacheManager.cleanup(), 60000);

// Memoized StockRow component
const StockRow = memo(({ stock, index }) => {
  // Format number with specified decimal places
  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined) return 'N/A';
    const num = parseFloat(value);
    return isNaN(num) ? 'N/A' : num.toFixed(decimals);
  };

  // Map backend fields to frontend expectations
  const change = getValue(stock.avg_growth || stock.change || stock.p_change, 0);
  const symbol = stock.symbol || 'N/A';
  const rank = stock.rank || index + 1;

  // Determine if change is positive or negative
  const changeVal = parseFloat(change) || 0;
  const isPositive = changeVal >= 0;
  const arrowColor = isPositive ? '#4CAF50' : '#F44336';
  const arrowSymbol = isPositive ? '↑' : '↓';

  return (
    <tr key={`${symbol}-${index}`} className="stock-row">
      <td className="stock-cell" style={{ textAlign: 'center' }}>{rank}</td>
      <td className="stock-cell" style={{ textAlign: 'center' }}>{symbol}</td>
      <td className={`stock-cell ${isPositive ? 'positive' : 'negative'}`} style={{ textAlign: 'center' }}>
        {formatNumber(change)}%
      </td>
      <td className="stock-cell" style={{ textAlign: 'center' }}>
        <span className="signal-arrow" style={{ color: arrowColor }}>
          {arrowSymbol}
        </span>
      </td>
    </tr>
  );
});

// Fallback sectors data
const FALLBACK_SECTORS = [
  'AUTO',
  'IT',
  'FMCG',
  'REALTY',
  'CEMENT',
  'PSU BANK',
  'METAL',
  'ENERGY',
  'PHARMA',
  'PVT BANK',
  'NIFTY50',
  'BANK NIFTY',
  'NIFTY MID SELECT',
  'FIN SERVICE',
  'LT',
  'SENSEX',
  'COUSUMER DURABLES'
];

// Main SectorRotation component
const SectorRotation = ({ onBack }) => {
  const fixedTimeframe = '1D';
  const [{ viewportWidth, viewportHeight }, setViewport] = useState(() => ({
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  const [sectorsData, setSectorsData] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [loadingSectorDetails, setLoadingSectorDetails] = useState(false);
  const [abnormalStocks, setAbnormalStocks] = useState([]);
  const [sectorStats, setSectorStats] = useState({ 
    total_stocks_analyzed: 0,
    last_updated: null
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const onResize = () => {
      setViewport({ viewportWidth: window.innerWidth, viewportHeight: window.innerHeight });
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const { positiveCount, negativeCount } = useMemo(() => {
    const counts = (abnormalStocks || []).reduce(
      (acc, stock) => {
        const changeVal = parseFloat(getValue(stock?.avg_growth || stock?.change || stock?.p_change, 0)) || 0;
        if (changeVal >= 0) acc.positive += 1;
        else acc.negative += 1;
        return acc;
      },
      { positive: 0, negative: 0 }
    );

    return { positiveCount: counts.positive, negativeCount: counts.negative };
  }, [abnormalStocks]);

  // Cached fetch function
  const cachedFetch = useCallback(async (endpoint, params = {}, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const cacheKey = cacheManager.getKey(endpoint, params);
    const cacheOptions = {
      expiry: options.expiry || 5 * 60 * 1000,
      forceRefresh: options.forceRefresh || false
    };

    if (!cacheOptions.forceRefresh) {
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) return cachedData;
    }
    
    if (!window.activeRequests) window.activeRequests = new Map();
    if (window.activeRequests.has(cacheKey)) {
      return window.activeRequests.get(cacheKey);
    }

    const requestPromise = (async () => {
      const maxRetries = options.retries || 2;
      let lastError;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${url}?${new URLSearchParams(params).toString()}`);
          
          if (!response.ok) {
            const errorText = await response.text().catch(() => 'No error details');
            const error = new Error(`HTTP ${response.status}: ${errorText}`);
            error.status = response.status;
            throw error;
          }
          
          const data = await response.json();
          if (data) {
            cacheManager.set(cacheKey, data, { expiry: cacheOptions.expiry });
          }
          return data;
          
        } catch (error) {
          lastError = error;
          console.error(`Attempt ${attempt + 1} failed:`, error);
          
          if (error.status >= 400 && error.status < 500 && error.status !== 429) {
            break;
          }
          
          if (attempt < maxRetries) {
            const baseDelay = 1000 * Math.pow(2, attempt);
            const jitter = Math.random() * 1000;
            const delay = Math.min(baseDelay + jitter, 10000);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      const cachedData = cacheManager.get(cacheKey);
      if (cachedData) {
        console.warn('Using cached data after failed request:', lastError);
        return cachedData;
      }
      
      throw lastError || new Error('Request failed');
    })();
    
    window.activeRequests.set(cacheKey, requestPromise);
    
    try {
      return await requestPromise;
    } finally {
      window.activeRequests.delete(cacheKey);
    }
  }, [API_BASE_URL]);

  // Fetch sector data
  const fetchSectorData = useCallback(async (sectorName) => {
    if (!sectorName) return;
    
    setLoadingSectorDetails(true);
    setError(null);
    
    if (selectedSector !== sectorName) {
      setAbnormalStocks([]);
    }
    
    try {
      const result = await cachedFetch(
        '/api/sector-abnormal-growth',
        { sector: sectorName, timeframe: fixedTimeframe },
        { forceRefresh: true, retries: 2 }
      );
      
      if (!result) throw new Error('No data received from the server');
      
      let stocks = [];
      let sectorStats = {};
      
      if (result.status === 'success' && result.data) {
        stocks = result.data.ranked_stocks || result.data.abnormal_growth_stocks || [];
        if ((!stocks || stocks.length === 0) && Array.isArray(result.data.stocks)) {
          stocks = result.data.stocks;
        }
        sectorStats = result.data.sector_stats || {};
      } else if (result.data) {
        // Handle direct data structure like your backend response
        stocks = result.data.ranked_stocks || result.data.abnormal_growth_stocks || [];
        sectorStats = result.data.sector_stats || {};
      } else if (Array.isArray(result)) {
        stocks = result;
      } else if (result.ranked_stocks || result.abnormal_growth_stocks) {
        // Handle case where result is the data object directly
        stocks = result.ranked_stocks || result.abnormal_growth_stocks || [];
        sectorStats = result.sector_stats || {};
      }
      
      setSelectedSector(sectorName);
      setSectorStats({
        total_stocks_analyzed: parseInt(sectorStats.total_stocks_analyzed) || stocks.length,
        last_updated: new Date().toISOString(),
        fromCache: false
      });
      
      if (stocks.length > 0) {
        // Map volatility to LTP if needed
        const stocksWithLTP = stocks.map(stock => ({
          ...stock,
          LTP: stock.volatility !== undefined ? stock.volatility : stock.LTP
        }));
        setAbnormalStocks(stocksWithLTP);
      } else {
        setAbnormalStocks([]);
        setError(`No data available for ${sectorName}`);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching sector data:', error);
      const cacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
        sector: sectorName,
        timeframe: fixedTimeframe
      });
      const cachedData = cacheManager.get(cacheKey);

      if (cachedData) {
        const cachedStocks = Array.isArray(cachedData.ranked_stocks) ? 
          cachedData.ranked_stocks : 
          (Array.isArray(cachedData.abnormal_growth_stocks) ? cachedData.abnormal_growth_stocks : []);
        const cachedStats = cachedData.sector_stats || {};

        // Map volatility to LTP if needed
        const stocksWithLTP = cachedStocks.map(stock => ({
          ...stock,
          LTP: stock.volatility !== undefined ? stock.volatility : stock.LTP
        }));

        setAbnormalStocks(stocksWithLTP);
        setSectorStats({
          total_stocks_analyzed: cachedStats.total_stocks_analyzed || 0,
          last_updated: 'Cached data',
          fromCache: true
        });
        setSelectedSector(sectorName);
        setError(`Using cached data. ${error.message}`);
        return cachedData;
      }
      setError(`Failed to load data: ${error.message}`);
      throw error;
    } finally {
      setLoadingSectorDetails(false);
    }
  }, [cachedFetch, fixedTimeframe]);

  // Refresh sector data
  const refreshSectorData = useCallback(() => {
    if (!selectedSector) return;

    const cacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
      sector: selectedSector,
      timeframe: fixedTimeframe
    });
    cacheManager.clear(cacheKey);
    fetchSectorData(selectedSector);
  }, [selectedSector, fetchSectorData, fixedTimeframe]);

  // Handle sector selection
  const handleSectorSelect = useCallback((sectorName) => {
    if (!sectorName) return;
    
    if (sectorName === selectedSector) {
      fetchSectorData(sectorName).catch(error => {
        console.error('Error refreshing sector data:', error);
        setError(`Failed to refresh data: ${error.message}`);
      });
      return;
    }
    
    setAbnormalStocks([]);
    setSelectedSector(sectorName);
    setSectorStats({
      total_stocks_analyzed: 0,
      last_updated: null
    });
    
    const oldCacheKey = cacheManager.getKey('/api/sector-abnormal-growth', {
      sector: selectedSector,
      timeframe: fixedTimeframe
    });
    cacheManager.clear(oldCacheKey);
    
    fetchSectorData(sectorName).catch(error => {
      console.error('Error loading sector data:', error);
      setError(`Failed to load data for ${sectorName}: ${error.message}`);
    });
  }, [selectedSector, fetchSectorData, fixedTimeframe]);

  // Load data when selectedSector changes
  useEffect(() => {
    if (!selectedSector) return;
    
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setLoadingSectorDetails(true);
        await fetchSectorData(selectedSector);
      } catch (error) {
        console.error('Error in useEffect:', error);
        if (isMounted) {
          setError(error.message || 'Failed to load sector data');
        }
      } finally {
        if (isMounted) {
          setLoadingSectorDetails(false);
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [selectedSector, fetchSectorData]);

  // Render sector bars chart (centered zero baseline; bars extend up for positive and down for negative)
  const renderSectorBars = () => {
    if (error) {
      return <div className="error-message">⚠️ {error}</div>;
    }

    if (loadingSectors) {
      return <div className="loading-message">⏳ Loading sectors data...</div>;
    }

    if (!sectorsData || sectorsData.length === 0) {
      return <div className="no-data">No sector data available</div>;
    }

    const sortedSectors = [...sectorsData].sort((a, b) => 
      (parseFloat(b.median_growth) || 0) - (parseFloat(a.median_growth) || 0)
    );

    // Determine the maximum absolute median growth so bars are scaled symmetrically
    const maxAbs = Math.max(
      1,
      ...sortedSectors.map(s => Math.abs(parseFloat(s.median_growth) || 0))
    );

    const isLandscape = viewportWidth > viewportHeight;
    const isPhone = viewportWidth <= 576;
    const isTablet = viewportWidth > 576 && viewportWidth <= 992;

    const totalTrackHeight = isLandscape && viewportHeight <= 520
      ? 180
      : isPhone
        ? 200
        : isTablet
          ? 240
          : 280; // Increased height to prevent label cutoff
    const halfTrack = totalTrackHeight / 2;

    const count = sortedSectors.length;
    const yAxisWidth = isPhone ? 30 : 42;
    const chromeWidth = isPhone ? 56 : isTablet ? 72 : 88; // padding + gaps + breathing room
    const usableWidth = Math.max(160, viewportWidth - yAxisWidth - chromeWidth);
    const slotWidth = usableWidth / Math.max(1, count);
    
    // Dynamic bar width calculation for desktop - make bars much thinner for better appearance
    const isDesktop = !isPhone && !isTablet;
    let barWidth;
    if (isDesktop) {
      // For desktop: keep bars thin like mobile (cap width regardless of screen size)
      const minBarWidth = 8;
      const maxBarWidth = 14;
      barWidth = Math.max(minBarWidth, Math.min(maxBarWidth, Math.floor(slotWidth * 0.25)));
    } else {
      // For mobile/tablet: keep existing logic
      barWidth = Math.max(6, Math.min(14, Math.floor(slotWidth * (isPhone ? 0.55 : 0.62))));
    }
    
    const valueFontSize = isPhone ? 10 : 12;
    const labelFontSize = isPhone ? 10 : isTablet ? 11 : 12;

    const ticks = [maxAbs, maxAbs / 2, 0, -maxAbs / 2, -maxAbs];
    const formatTick = (v) => {
      const rounded = Math.round(v * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
    };

    return (
      <div className="vertical-sector-chart" style={{ gap: isPhone ? '10px' : undefined, padding: isPhone ? '10px 8px' : undefined }}>
        <div className="bar-row" style={{ gap: isPhone ? '2px' : undefined }}>
          {sortedSectors.map((sector) => {
            if (!sector) return null;

            const growth = parseFloat(sector.median_growth) || 0;
            const isPositive = growth >= 0;
            const magnitude = Math.abs(growth);
            const barPixels = Math.max(2, (magnitude / maxAbs) * halfTrack);
            const formattedGrowth = growth.toFixed(2);
            const barColor = isPositive ? "#6aa6ff" : "#ff6b6b";

            // Dynamic width calculation based on percentage magnitude for desktop
            let dynamicBarWidth = barWidth;
            if (isDesktop) {
              dynamicBarWidth = barWidth;
            }

            return (
              <div
                key={sector.name}
                className="vertical-bar-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: `${totalTrackHeight + 80}px`, // Increased from 60 to 80 for label space
                  padding: isPhone ? '4px 0' : '8px 0', // Increased padding
                }}
              >
                <div style={{ position: 'relative', height: `${totalTrackHeight}px`, width: '100%', maxWidth: `${dynamicBarWidth + 8}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '2px', background: 'var(--card-border, #e0e0e0)', transform: 'translateY(-50%)' }} />

                  <div
                    className={`vertical-bar ${selectedSector === sector.name ? 'selected' : ''}`}
                    onClick={() => handleSectorSelect(sector.name)}
                    title={`${sector.name}: ${formattedGrowth}%`}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: `${dynamicBarWidth}px`,
                      height: `${barPixels}px`,
                      background: barColor,
                      borderRadius: isPositive ? '4px 4px 0 0' : '0 0 4px 4px', // Rounded top for positive, rounded bottom for negative
                      transition: 'height 0.35s ease, background-color 0.25s ease, width 0.35s ease',
                      cursor: 'pointer',
                      bottom: isPositive ? '50%' : undefined,
                      top: isPositive ? undefined : '50%',
                      opacity: isDesktop ? 0.85 : 0.68
                    }}
                  />

                  <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: `${valueFontSize}px`, fontWeight: '600', color: barColor, whiteSpace: 'nowrap', bottom: isPositive ? `calc(50% + ${barPixels}px + 8px)` : undefined, top: isPositive ? undefined : `calc(50% + ${barPixels}px + 8px)`, writingMode: isPhone ? 'vertical-rl' : 'horizontal-tb', transform: isPhone ? 'rotate(180deg) translateX(-50%)' : 'translateX(-50%)', textAlign: 'center' }}>
                    {formattedGrowth}%
                  </div>
                </div>

                <div
                  className="sector-label"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSectorSelect(sector.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSectorSelect(sector.name);
                    }
                  }}
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    textAlign: 'center',
                    fontSize: `${labelFontSize}px`,
                    color: 'var(--text-secondary, #666)',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '8px',
                    cursor: 'pointer',
                  }}
                >
                  {sector.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSectorGrowthChart = () => null;

  // Render stock table
  const renderStockTable = () => {
    if (loadingSectorDetails) {
      return <div className="loading-message">⏳ Loading {selectedSector} data...</div>;
    }

    if (!abnormalStocks || abnormalStocks.length === 0) {
      return (
        <div className="no-data-message">
          {error ? (
            <div className="error-message">⚠️ {error}</div>
          ) : (
            <>
              No stock data available for {selectedSector || 'the selected sector'}.
              <div className="debug-info">
                <div>Stocks in state: {abnormalStocks ? abnormalStocks.length : '0'}</div>
              </div>
            </>
          )}
        </div>
      );
    }

    return (
      <div className="stock-table">
        <table>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '38%' }} />
            <col style={{ width: '32%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ textAlign: 'center' }}>Rank</th>
              <th style={{ textAlign: 'center' }}>Symbol</th>
              <th style={{ textAlign: 'center' }}>Change</th>
              <th style={{ textAlign: 'center' }}>Signal</th>
            </tr>
          </thead>
          <tbody>
            {abnormalStocks.map((stock, index) => (
              <StockRow key={`${stock.symbol}-${index}`} stock={stock} index={index} />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  useEffect(() => {
    let cancelled = false;

    const loadAllSectorsData = async () => {
      setLoadingSectors(true);
      setError('');

      try {
        const sectorsResponse = await cachedFetch('/api/sectors', {}, {
          expiry: 60 * 60 * 1000,
          retries: 2
        });

        let sectorsArr = [];
        if (sectorsResponse && Array.isArray(sectorsResponse)) {
          sectorsArr = sectorsResponse;
        } else if (sectorsResponse && sectorsResponse.status === 'success' && Array.isArray(sectorsResponse.sectors)) {
          sectorsArr = sectorsResponse.sectors;
        } else if (sectorsResponse && sectorsResponse.data && Array.isArray(sectorsResponse.data)) {
          sectorsArr = sectorsResponse.data;
        } else {
          sectorsArr = FALLBACK_SECTORS;
        }

        const sectorsWithGrowth = await Promise.all(
          sectorsArr.map(async (sector) => {
            const sectorName = typeof sector === 'object' ? sector.name || sector : sector;

            try {
              const response = await cachedFetch(
                '/api/sector-abnormal-growth',
                { sector: sectorName, timeframe: fixedTimeframe },
                { expiry: 15 * 60 * 1000 }
              );

              if (response && response.data && response.data.sector_stats) {
                return {
                  name: sectorName,
                  median_growth: parseFloat(response.data.sector_stats.median_growth) || 0,
                  total_stocks: parseInt(response.data.sector_stats.total_stocks_analyzed) || 0,
                  last_updated: response.data.sector_stats.timestamp || new Date().toISOString()
                };
              } else if (response && response.sector_stats) {
                return {
                  name: sectorName,
                  median_growth: parseFloat(response.sector_stats.median_growth) || 0,
                  total_stocks: parseInt(response.sector_stats.total_stocks_analyzed) || 0,
                  last_updated: response.sector_stats.timestamp || new Date().toISOString()
                };
              }

              return {
                name: sectorName,
                median_growth: 0,
                total_stocks: 0,
                last_updated: new Date().toISOString()
              };
            } catch (err) {
              return {
                name: sectorName,
                median_growth: 0,
                total_stocks: 0,
                last_updated: new Date().toISOString(),
                error: err.message
              };
            }
          })
        );

        if (cancelled) return;

        const sortedSectors = [...sectorsWithGrowth].sort((a, b) => b.median_growth - a.median_growth);

        setSectorsData(sortedSectors);

        if (sortedSectors.length > 0 && !selectedSector) {
          setSelectedSector(sortedSectors[0].name);
        }

      } catch (err) {
        if (!cancelled) {
          const fallbackSectors = FALLBACK_SECTORS.map(sector => ({
            name: sector,
            median_growth: (Math.random() * 10 - 5).toFixed(2),
            total_stocks: 0,
            last_updated: new Date().toISOString()
          })).sort((a, b) => b.median_growth - a.median_growth);

          setSectorsData(fallbackSectors);

          if (fallbackSectors.length > 0 && !selectedSector) {
            setSelectedSector(fallbackSectors[0].name);
          }

          setError('Using fallback sector data with sample growth values.');
        }
      } finally {
        if (!cancelled) {
          setLoadingSectors(false);
        }
      }
    };

    if (sectorsData.length === 0 || !sectorStats.last_updated) {
      loadAllSectorsData();
    }

    return () => { cancelled = true; };
  }, [cachedFetch, fixedTimeframe]);

  return (
    <div className="sector-rotation-page">
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="header-actions">
            <div className="last-updated">
              {sectorStats.last_updated && (
                <span>
                  {sectorStats.fromCache ? 'Cached: ' : 'Updated: '}
                  {new Date(sectorStats.last_updated).toLocaleString()}
                </span>
              )}
            </div>
            <RefreshButton 
              onRefresh={refreshSectorData}
              isLoading={loadingSectorDetails}
              disabled={!selectedSector}
              size="small"
              style={{ marginRight: '10px' }}
            />
          </div>
        </div>
      </header>

      <div className="sector-rotation-container">
        <div className="sector-rotation-header s4u-title-row">
          <div className="s4u-page-heading">
            <button className="back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
            <h2>Sector Rotation</h2>
          </div>
        </div>

        <div className="chart-panel">
          <div className="chart-title">
            <h3>📊 Sector Performance</h3>
            <div className="sector-legend">
              <span className="legend-item">
                <span className="legend-color positive"></span> Positive
              </span>
              <span className="legend-item">
                <span className="legend-color negative"></span> Negative
              </span>
            </div>
          </div>

          
          {renderSectorBars()}
          
          {error && <div className="error-message">⚠️ {error}</div>}
        </div>

        {selectedSector && (
          <div className="sector-details">
            <div className="sector-header">
              <h3>📈 {selectedSector} - Top Stocks</h3>
              <div className="sector-stats">
                <span className="stat">
                  <span className="stat-label">Stocks:</span>
                  <span className="stat-value">{sectorStats.total_stocks_analyzed}</span>
                </span>
              </div>
            </div>

            <div className="summary-bar">
              <div className="summary-badge positive-badge">
                <span className="badge-label">Net Positive</span>
                <span className="badge-value">({positiveCount})</span>
              </div>
              <h3 className="summary-title">Today Sector Mover</h3>
              <div className="summary-badge negative-badge">
                <span className="badge-label">Net Negative</span>
                <span className="badge-value">({negativeCount})</span>
              </div>
            </div>
            
            {renderSectorGrowthChart()}
            {renderStockTable()}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectorRotation;